import React from 'react';
import { Modal, Spinner } from '../../components/ui';
import { sideChipClass, inviteChipClass, visibilityChipClass, fullName, INVITE_STATUSES } from '../../utils/helpers';
import MemberForm from './MemberForm';

const functionNames = (row, fnName) => String(row.FunctionNames || row.FunctionName || fnName(row.FunctionId))
  .split(',')
  .map(value => value.trim())
  .filter(Boolean);

function FunctionCell({ row, fnName }) {
  const names = functionNames(row, fnName);
  if (names.length <= 1) return names[0] || '-';
  return (
    <div className="guest-function-chips">
      {names.map((name, index) => (
        <span key={`${name}-${index}`} className={`function-chip function-chip-${index % 5}`}>
          {name}
        </span>
      ))}
    </div>
  );
}

// Desktop UI for Members — wide table.
export default function MembersDesktop({ vm }) {
  const {
    isAdmin, canAdd, canEdit, canDelete, mine, functions, loading, filtered, stats,
    q, setQ, fnFilter, setFnFilter, fnName, adderCount, openAdd, openEdit, mark, del, open,
    inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    isMarriage, sort, setSort, toggleSort, SORT_OPTIONS, canBulkImport,
  } = vm;

  // Column header that sorts on click; shows ▲/▼ on the active column.
  const Th = ({ k, children, className, title }) => (
    <th className={(className || '') + ' sortable' + (sort.key === k ? ' active' : '')} title={title || 'Sort'}
      onClick={() => toggleSort(k)}>
      {children}{sort.key === k && <span className="arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="guests-page">
      <div className="page-head guest-controls-sticky">
        <h1>Guests</h1>
        <span className="muted">{stats.parties}</span>
        <div className="spacer" />
        <select value={fnFilter} onChange={e => setFnFilter(e.target.value)} title="Filter by function"
          style={{ padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 10, maxWidth: 220 }}>
          <option value="">All functions</option>
          {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName}</option>)}
        </select>
        <select value={sort.key} onChange={e => setSort(s => ({ key: e.target.value, dir: s.dir }))} title="Sort by"
          style={{ padding: '9px 10px', border: '1px solid var(--line)', borderRadius: 10, maxWidth: 170 }}>
          {SORT_OPTIONS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
        </select>
        <button className="btn light" title={sort.dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
          onClick={() => setSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))} style={{ padding: '9px 10px' }}>
          {sort.dir === 'asc' ? 'A→Z' : 'Z→A'}
        </button>
        <input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 10, maxWidth: 180 }} />
        {canAdd && <button className="btn primary" onClick={() => openAdd('individual')} disabled={!functions.length}>+ Add Guest</button>}
        {canAdd && <button className="btn light" onClick={() => openAdd('family')} disabled={!functions.length}>+ Add Family</button>}
      </div>

      {!functions.length && (
        <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
          No functions yet — an Event Admin must add functions before guests can be invited.
        </div>
      )}

      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        <strong>{stats.parties}</strong> parties · <strong>{stats.persons}</strong> persons expected
        {stats.entries !== stats.parties ? ` · ${stats.entries} function invites` : ''}
        {fnFilter ? ` for ${fnName(fnFilter)}` : ' (all functions)'}
        {isAdmin ? ' — full list across all members.' : ' — guests you added plus anything shared as Common.'}
        {canAdd && !canBulkImport && <span> · 📱 To add many people at once, use <strong>Add from Contacts</strong> in the mobile app.</span>}
      </div>

      <div className="card table-wrap guests-table-wrap">
        {loading ? <Spinner /> : filtered.length === 0 ? (
          <div className="empty">No guests yet.</div>
        ) : (
          <table className="tbl compact guests-table">
            <thead>
              <tr>
                <Th k="name" className="grow">Guest</Th>
                <Th k="mobile">Mobile</Th>
                {!fnFilter && <Th k="function">Function</Th>}
                {isMarriage && <Th k="side">Side</Th>}
                <Th k="pax" className="num" title="Persons expected — sort">Pax</Th>
                <Th k="visibility">Visibility</Th>
                {isAdmin && <Th k="addedBy">Added by</Th>}
                <Th k="status">Status</Th>
                <Th k="rsvp">RSVP</Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const editable = mine(row);
                const count = adderCount(row);
                // Second line under the name: whatever detail exists, joined compactly.
                const detail = [row.Relation, row.GroupName, row.City].filter(Boolean).join(' · ');
                const rsvp = row.RSVPStatus && row.RSVPStatus !== 'Pending'
                  ? `${row.RSVPStatus}${row.ConfirmedCount ? ` (${row.ConfirmedCount})` : ''}` : '—';
                return (
                  <tr key={row.GuestId} className={editable && canEdit ? 'row-link' : undefined}
                    title={editable && canEdit ? 'Open this guest' : undefined}
                    onClick={editable && canEdit ? () => openEdit(row) : undefined}>
                    <td className="grow">
                      <div style={{ fontWeight: 600 }}>{fullName(row)}</div>
                      {(detail || row.Remarks) && (
                        <div className="sub" title={row.Remarks || undefined}>
                          {detail}{detail && row.Remarks ? ' · ' : ''}{row.Remarks ? <em>{row.Remarks}</em> : null}
                        </div>
                      )}
                      {!isAdmin && (
                        <div className="sub">Added by {(row.AddedByName || row.InvitedByName || '').trim() || '—'}</div>
                      )}
                    </td>
                    <td>{row.MobileNumber || '—'}</td>
                    {!fnFilter && <td><FunctionCell row={row} fnName={fnName} /></td>}
                    {isMarriage && <td><span className={sideChipClass(row.Side)}>{row.Side}</span></td>}
                    <td className="num">{row.ExpectedCount ?? 1}</td>
                    <td><span className={visibilityChipClass(row.Visibility)}>{row.Visibility || 'Private'}</span></td>
                    {isAdmin && (
                      <td>
                        {(row.AddedByName || row.InvitedByName || '').trim() || '—'}
                        {count > 1 && <span className="chip warn" style={{ marginLeft: 6 }} title={`${count} members invited this guest`}>×{count}</span>}
                      </td>
                    )}
                    <td onClick={(e) => e.stopPropagation()}>
                      {editable && canEdit ? (
                        <select value={row.InviteStatus || 'Pending'} onChange={e => mark(row, e.target.value)}
                          className={inviteChipClass(row.InviteStatus)} title={row.InvitedVia ? `via ${row.InvitedVia}` : undefined}>
                          {INVITE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <span className={inviteChipClass(row.InviteStatus)}>{row.InviteStatus}</span>
                      )}
                    </td>
                    <td className="num">{rsvp}</td>
                    <td className="guest-row-actions" style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <div className="guest-actions-inner">
                        {row.MobileNumber && (
                          <button className="btn primary sm invite-action-btn" title="Invite on WhatsApp" onClick={() => openInvite(row)}>
                            Invite
                          </button>
                        )}
                        {editable && canEdit && <button className="icon-btn" title="Edit" onClick={() => openEdit(row)}>✎</button>}
                        {editable && canDelete && <button className="icon-btn danger" title="Delete" onClick={() => del(row)}>🗑</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {open && <MemberForm vm={vm} />}
      {inviteRow && (
        <Modal title="Invite on WhatsApp" onClose={closeInvite} footer={<button className="btn light" onClick={closeInvite} disabled={!!inviteSharing}>Cancel</button>}>
          <div style={{ display: 'grid', gap: 10 }}>
            {inviteCooldown > 0 && (
              <div className="invite-cooldown-note">
                Please wait {inviteCooldown} seconds before sending another invitation.
              </div>
            )}
            <button className="btn primary" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('image')}>
              {inviteSharing === 'image' ? 'Opening...' : 'Image Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0} onClick={() => sendInvite('text')}>
              {inviteSharing === 'text' ? 'Opening...' : 'Text Invite'}
            </button>
            <button className="btn light" disabled={!!inviteSharing || inviteCooldown > 0 || !guestHasAllFunctions(inviteRow)} onClick={() => sendInvite('pdf')}>
              {inviteSharing === 'pdf' ? 'Opening...' : 'PDF Invite'}
            </button>
            {!guestHasAllFunctions(inviteRow) && (
              <div className="muted" style={{ fontSize: 12 }}>PDF invite is available only when this guest is invited to all functions.</div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
